/**
 * What has been done to this record, and by whom (§§4.36, 11.53).
 *
 * The read side of the audit trail, which until M2 was written to on every change
 * and never once looked at. §11.53 makes this a demonstration moment: a member of
 * staff points at a record and asks *"who changed this, when, and why?"* — and
 * the answer is on the screen, not in a database client.
 *
 * **A timeline, not a table.** Each entry is one event: what happened, who did
 * it, when, what moved, and — where the system insisted on one — why. A table
 * with an `old_values` column would be the same information arranged so that
 * nobody reads it.
 *
 * **Newest first**, because the question is almost always "what happened to this
 * *recently*". Ordered by `(changed_at, audit_seq)` on the server: rows written
 * in one transaction share a timestamp exactly, so time alone does not order them
 * (migration `014`).
 *
 * Shared by volunteers and attendance. The two differ only in which endpoint
 * feeds them, and a history panel that looked different depending on what it was
 * describing would be two things to learn instead of one.
 */

import type { AuditEntry, ReferenceValue } from '@parinaam-vms/shared';
import { Card, EmptyState, ErrorState, LoadingState } from '../common';
import { formatDateTime } from '../../utils/format';
import { actualChanges, summarize } from './describeChange';
import styles from './HistoryPanel.module.css';

export interface HistoryPanelProps {
  entries: AuditEntry[] | undefined;
  /** `isPending`, not `isLoading` — see the rule in States.tsx. */
  isPending: boolean;
  error: unknown;
  onRetry: () => void;
  /** Used to show "Weekends, Flexible" where the row stores `WEEKENDS,FLEXIBLE`. */
  reference: Record<string, ReferenceValue[]> | undefined;
  /** What this record is, for the empty state: "this volunteer", "these hours". */
  subject: string;
}

export function HistoryPanel({
  entries,
  isPending,
  error,
  onRetry,
  reference,
  subject,
}: HistoryPanelProps) {
  return (
    <Card title="History">
      {isPending ? (
        <LoadingState label="Loading history…" />
      ) : error ? (
        <ErrorState error={error} onRetry={onRetry} />
      ) : !entries || entries.length === 0 ? (
        <EmptyState
          title="Nothing recorded yet"
          message={`Every change to ${subject} is kept here, with who made it and why.`}
        />
      ) : (
        <ol className={styles.timeline}>
          {entries.map((entry) => (
            <Entry key={entry.audit_id} entry={entry} reference={reference} />
          ))}
        </ol>
      )}
    </Card>
  );
}

function Entry({
  entry,
  reference,
}: {
  entry: AuditEntry;
  reference: Record<string, ReferenceValue[]> | undefined;
}) {
  const changes = actualChanges(entry, reference);

  return (
    <li className={styles.entry}>
      <div className={styles.marker} aria-hidden="true" />

      <div className={styles.body}>
        <p className={styles.summary}>
          <strong>{summarize(entry)}</strong>
          <span className={styles.meta}>
            {' '}
            · {formatDateTime(entry.changed_at)}
            {/*
              The actor, when there is one. `changed_by` is null only for rows
              written before actor tracking, and "by null" is worse than silence.
            */}
            {entry.changed_by ? ` · by ${entry.changed_by}` : ''}
          </span>
        </p>

        {changes.length > 0 && (
          <ul className={styles.changes}>
            {changes.map((change) => (
              <li key={change.label}>
                <span className={styles.field}>{change.label}</span>{' '}
                <span className={styles.from}>{change.from}</span>
                <span className={styles.arrow} aria-label="changed to">
                  {' → '}
                </span>
                <span className={styles.to}>{change.to}</span>
              </li>
            ))}
          </ul>
        )}

        {/*
          The reason, where one was required (§§4.28, 8.6.3). Quoted rather than
          paraphrased: somebody wrote this sentence about this record, and it is
          the most useful thing on the row.
        */}
        {entry.reason && <p className={styles.reason}>“{entry.reason}”</p>}
      </div>
    </li>
  );
}
